import { Head } from 'vite-react-ssg';
import { Link } from "react-router-dom";
import { Check, ArrowRight, BookOpen } from "lucide-react";
import Breadcrumbs from "@/components/Breadcrumbs";

const DeepseekCorrectSpelling = () => {
  const variants = [
    { wrong: "deepsek", note: "Missing 'e' — by far the most common typo (search volume ~18K/month in the US)." },
    { wrong: "deepseak", note: "Extra 'a' — phonetic misspelling. Sometimes intentional brand styling, but always incorrect." },
    { wrong: "deep sek", note: "Two words with missing 'e' — combines both common errors." },
    { wrong: "deap seek", note: "'Deap' instead of 'deep' — phonetic typo from non-native English speakers." },
    { wrong: "deepsearch", note: "Confusion with the Perplexity feature 'Deep Search'. Different product." },
    { wrong: "deep-seek", note: "Hyphenated — incorrect; the official brand never uses a hyphen." },
    { wrong: "DeepSEEK", note: "All caps on 'SEEK' — the official capitalisation is camelCase: DeepSeek." },
    { wrong: "deep seek", note: "Two words — extremely common (1M+ monthly impressions). Search engines treat this as a valid variant but the brand is one word." },
  ];

  const faqItems = [
    { q: "Is it DeepSeek or Deep Seek?", a: "The official brand name is DeepSeek — one word, camelCase. 'Deep Seek' as two words is a common spelling variant that search engines recognise, but the company itself never writes it that way." },
    { q: "Is 'deepsek' the same as DeepSeek?", a: "Yes — 'deepsek' is the most common typo for DeepSeek. People search for it about 18,000 times per month in the US alone. There is no separate product called Deepsek." },
    { q: "How do you pronounce DeepSeek?", a: "DeepSeek is pronounced 'deep-seek' — exactly like the two English words 'deep' and 'seek' read together. The 'ee' sounds are long, as in 'meet' and 'feet'." },
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": "https://deepseek.ai/deepseek-correct-spelling",
        "url": "https://deepseek.ai/deepseek-correct-spelling",
        "name": "Deepsek or DeepSeek? Correct Spelling Explained",
        "description": "The correct spelling is DeepSeek — one word, camelCase. Common typos include deepsek, deepseak, deap seek and deep sek.",
        "inLanguage": "en"
      },
      {
        "@type": "FAQPage",
        "mainEntity": faqItems.map(item => ({
          "@type": "Question",
          "name": item.q,
          "acceptedAnswer": { "@type": "Answer", "text": item.a }
        }))
      }
    ]
  };

  return (
    <>
      <Head>
        <title>Deepsek or DeepSeek? Correct Spelling &amp; Common Typos</title>
        <meta name="description" content="The correct spelling is DeepSeek — one word, camelCase. See common misspellings like deepsek, deepseak, deap seek and deep sek." />
        <meta name="keywords" content="deepsek, deepseak, deep sek, deap seek, deepsearch, how to spell deepseek, deepseek correct spelling" />
        <link rel="canonical" href="https://deepseek.ai/deepseek-correct-spelling" />
        <meta property="og:title" content="Deepsek or DeepSeek? Correct Spelling Explained" />
        <meta property="og:description" content="DeepSeek is the correct spelling. Here are 8 common typos and what they all refer to." />
        <meta property="og:url" content="https://deepseek.ai/deepseek-correct-spelling" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content="https://deepseek.ai/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Deepsek or DeepSeek? Correct Spelling Explained" />
        <meta name="twitter:description" content="DeepSeek is the correct spelling. Here are 8 common typos and what they all refer to." />
        <meta name="twitter:image" content="https://deepseek.ai/og-image.png" />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Head>

      <main className="min-h-screen bg-background">
        <section className="py-16 bg-gradient-to-br from-primary/5 via-background to-primary/10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Breadcrumbs currentPage="Correct Spelling" />
            <div className="mt-8">
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
                <BookOpen className="w-4 h-4 mr-2" /> Spelling Guide
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
                Is it Deepsek or DeepSeek? Here's the correct spelling
              </h1>
              <p className="text-xl text-muted-foreground mb-8">
                The correct spelling is <strong className="text-primary">DeepSeek</strong> — one word, camelCase, capital D and capital S. Everything else (deepsek, deepseak, deap seek, deep sek) is a typo for the same Chinese AI company.
              </p>
              <div className="p-6 rounded-xl bg-card border-2 border-primary mb-8">
                <div className="flex items-center gap-3 mb-2">
                  <Check className="w-6 h-6 text-primary" />
                  <span className="text-sm font-semibold text-primary">CORRECT</span>
                </div>
                <p className="text-3xl font-bold text-foreground">DeepSeek</p>
                <p className="text-sm text-muted-foreground mt-2">One word · Capital D · Capital S · No hyphen · No space</p>
              </div>
              <Link to="/" className="inline-flex items-center px-6 py-3 rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90">
                Visit DeepSeek homepage <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </div>
          </div>
        </section>

        <section className="py-16 bg-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-foreground mb-8">Common misspellings of DeepSeek</h2>
            <div className="space-y-4">
              {variants.map(v => (
                <div key={v.wrong} className="p-5 rounded-lg bg-card border border-border">
                  <div className="flex flex-wrap items-baseline gap-3 mb-2">
                    <span className="text-2xl font-bold text-muted-foreground line-through">{v.wrong}</span>
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                    <span className="text-2xl font-bold text-primary">DeepSeek</span>
                  </div>
                  <p className="text-muted-foreground">{v.note}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 bg-muted/30">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-foreground mb-6">Why so many people misspell DeepSeek</h2>
            <p className="text-lg text-muted-foreground mb-4">
              DeepSeek launched into global awareness in January 2025 when its R1 reasoning model topped benchmarks at a fraction of OpenAI's cost. Hundreds of millions of people heard the name spoken before they ever saw it written — and "deep seek" is naturally easy to mis-type as "deepsek" (skipping the second 'e') or "deepseak" (adding an 'a' to match the spoken stress).
            </p>
            <p className="text-lg text-muted-foreground mb-4">
              The official company name in English is <strong>DeepSeek</strong>, sometimes written as <strong>DeepSeek AI</strong>. In Chinese it is 深度求索 (Shēndù Qiúsuǒ), which literally translates to "deep search" or "deep seek" — exactly the origin of the English brand.
            </p>
            <p className="text-lg text-muted-foreground">
              Whichever spelling brought you here, you've landed in the right place. Continue to our{" "}
              <Link to="/blog/what-is-deepseek-ai" className="text-primary hover:underline">DeepSeek AI guide</Link> or jump straight into the{" "}
              <Link to="/chat" className="text-primary hover:underline">free chat</Link>.
            </p>
          </div>
        </section>

        <section className="py-16 bg-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-foreground mb-8">FAQ</h2>
            <div className="space-y-4">
              {faqItems.map((item, i) => (
                <div key={i} className="p-6 rounded-lg bg-card border border-border">
                  <h3 className="font-semibold text-foreground mb-2">{item.q}</h3>
                  <p className="text-muted-foreground">{item.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 bg-primary">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-primary-foreground mb-6">Now you've got the spelling right — try DeepSeek</h2>
            <Link to="/" className="inline-flex items-center px-8 py-4 rounded-lg bg-white text-primary font-semibold hover:bg-gray-100">
              Go to DeepSeek <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </section>
      </main>
    </>
  );
};

export default DeepseekCorrectSpelling;
